/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { all, takeLeading, put } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  ActionRequest,
  ActionSuccess,
  PpvEntitlement,
  ActionFail,
  XError,
  Constants,
  EntitlementApi,
  Ducks,
} from '@wit/vtv-x-foundation-lib';
import { StoreState } from 'store/store';

export enum ActionTypes {
  REQUEST = 'entitlements/ppv/REQUEST',
  SUCCESS = 'entitlements/ppv/SUCCESS',
  FAIL = 'entitlements/ppv/FAIL',
}

type Request = ActionRequest<ActionTypes.REQUEST>;
type Success = ActionSuccess<ActionTypes.SUCCESS, PpvEntitlement[]>;
type Fail = ActionFail<ActionTypes.FAIL>;

type Actions = Request | Success | Fail;

const Actions = {
  request: (): Request => ({ type: ActionTypes.REQUEST }),
  success: (payload: PpvEntitlement[]): Success => ({ type: ActionTypes.SUCCESS, payload }),
  fail: (error: XError): Fail => ({ type: ActionTypes.FAIL, error }),
};

export const ActionCreators = {
  request: (): Request => Actions.request(),
};

export interface ReducerState {
  data: PpvEntitlement[];
  error?: XError;
}

const initialState: ReducerState = {
  data: [],
};

export default function channelsReducer(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionTypes.REQUEST:
      return {
        ...state,
      };
    case ActionTypes.SUCCESS: {
      return {
        data: action.payload,
      };
    }
    case ActionTypes.FAIL:
      return {
        ...state,
        error: action.error,
      };
    default:
      return state;
  }
}

// SELECTORS
export const Selectors = {
  getPpvEntitlements: (state: StoreState): PpvEntitlement[] => {
    return state.entitlements.ppv.data;
  },
  getError: (state: StoreState): XError | undefined => state.entitlements.ppv.error,
};

export const PAGINATED_ENTITLEMENTS_NAMESPACE = 'ENTITLEMENTS_PPV';

function* requestPpvEntitlementsSaga(): SagaIterator {
  try {
    const result: PpvEntitlement[] = yield Ducks.Pagination.callCompletedPaginatedApi(
      PAGINATED_ENTITLEMENTS_NAMESPACE,
      Constants.ENTITLEMENTS_API_PAGE_SIZE,
      EntitlementApi.listPpvHouseholdEntitlements
    );

    yield put(Actions.success(result));
  } catch (error) {
    yield put(Actions.fail(XError.fromError(error)));
  }
}

export function* saga(): SagaIterator {
  yield all([takeLeading(ActionTypes.REQUEST, requestPpvEntitlementsSaga)]);
}
